import React, { FC, ChangeEvent, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Radio, theme } from '@blueprint/ui';

import {
    updateFilter,
    propertyTypeSelector,
    propertySubTypesSelector,
    includeSharedAccommodationSelector
} from '../../reducers/filters';
import { SearchPageType } from '../SearchContext';
import {
    FiltersCheckBox,
    RadioButtonGroup,
    SometimesInlineFiltersCheckBox
} from './Filters.styles';
import { listItemToggle } from '../helpers';
import { FilterOptions } from '../../reducers/filters.types';

type IndentedSectionProps = {
    isActive: boolean;
};

const IndentedSection = styled('div')<IndentedSectionProps>(
    ({ isActive }) => css`
        padding-left: ${theme.spacing['32px']};
        margin-bottom: ${theme.spacing['24px']};
        opacity: ${isActive ? 1 : 0.5};

        ${SometimesInlineFiltersCheckBox} {
            ${theme.mq.small} {
                width: calc(50% - ${theme.spacing['16px']});
            }
        }
    `
);

type PropertyTypeControlProps = {
    propertyTypeOptions: FilterOptions;
    propertyType: string | string[];
    onChangePropertyType: (evt: ChangeEvent<HTMLInputElement>) => void;
    propertySubTypes: string | string[];
    propertySubTypeOptions: FilterOptions;
    onChangePropertySubType: (evt: ChangeEvent<HTMLInputElement>) => void;
};

const PropertyTypeControls: FC<PropertyTypeControlProps> = ({
    propertyTypeOptions,
    onChangePropertyType,
    propertyType,
    propertySubTypeOptions,
    onChangePropertySubType,
    propertySubTypes
}) => {
    return (
        <>
            {propertyTypeOptions?.map((propertyTypeOption) => {
                const { label, value } = propertyTypeOption;

                const idKey = `filters_modal_property_type_option${
                    value ? `_${value}` : '_all'
                }`;
                const displayLabel =
                    label === 'Show all' ? 'All property types' : label;
                const hasSelectedThisPropertyType = propertyType === value;

                return (
                    <Fragment key={value}>
                        <Radio
                            id={idKey}
                            key={`${value}-${label}`}
                            data-testid={value}
                            name="option"
                            label={displayLabel}
                            value={value}
                            checked={hasSelectedThisPropertyType}
                            onChange={onChangePropertyType}
                        />
                        {label === 'Houses' && (
                            <IndentedSection
                                isActive={propertyType === 'houses'}
                                key="property-sub-types"
                            >
                                {propertySubTypeOptions?.map(
                                    (propertySubTypeOption, index) => {
                                        const {
                                            value,
                                            label
                                        } = propertySubTypeOption;

                                        const isChecked = propertySubTypes.includes(
                                            value
                                        );

                                        return (
                                            <SometimesInlineFiltersCheckBox
                                                id={value}
                                                key={`${value}-${index}`}
                                                value={value}
                                                onChange={
                                                    onChangePropertySubType
                                                }
                                                label={label}
                                                checked={isChecked}
                                            />
                                        );
                                    }
                                )}
                            </IndentedSection>
                        )}
                    </Fragment>
                );
            })}
        </>
    );
};

interface Props {
    searchPageType: SearchPageType;
}

export const SelectPropertyType: FC<Props> = ({ searchPageType }) => {
    const reduxDispatch = useDispatch();
    const {
        selectedValue: propertyType,
        options: propertyTypeOptions
    } = useSelector(propertyTypeSelector);
    const {
        selectedValue: propertySubTypes,
        options: propertySubTypeOptions
    } = useSelector(propertySubTypesSelector);

    const {
        label: includeSharedAccommodationLabel,
        selectedValue: includeSharedAccommodationSelectedValue
    } = useSelector(includeSharedAccommodationSelector);

    const onChangePropertyType = (evt: ChangeEvent<HTMLInputElement>) => {
        reduxDispatch(updateFilter('propertyType', evt.currentTarget.value));
    };

    const onChangePropertySubType = (evt: ChangeEvent<HTMLInputElement>) => {
        reduxDispatch(updateFilter('propertyType', 'houses'));
        const newPropertySubTypes = listItemToggle(
            propertySubTypes,
            evt.currentTarget.value
        );
        reduxDispatch(updateFilter('propertySubTypes', newPropertySubTypes));
    };

    const onIncludeSharedAccommodationChange = (
        evt: ChangeEvent<HTMLInputElement>
    ) => {
        reduxDispatch(
            updateFilter(
                'includeSharedAccommodation',
                evt.currentTarget.checked
            )
        );
    };

    return (
        <>
            <RadioButtonGroup>
                <PropertyTypeControls
                    propertyTypeOptions={propertyTypeOptions}
                    propertyType={propertyType}
                    onChangePropertyType={onChangePropertyType}
                    propertySubTypeOptions={propertySubTypeOptions}
                    propertySubTypes={propertySubTypes}
                    onChangePropertySubType={onChangePropertySubType}
                />
            </RadioButtonGroup>

            {searchPageType === 'to-rent' && (
                <FiltersCheckBox
                    id="filters-modal_include-shared"
                    data-testid="filters-modal_include-shared"
                    label={includeSharedAccommodationLabel}
                    checked={includeSharedAccommodationSelectedValue}
                    onChange={onIncludeSharedAccommodationChange}
                />
            )}
        </>
    );
};
